import { TrashIcon } from "assets/svg/trash";
import { UnlinkIcon } from "assets/svg/unlink";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import {
  Button,
  Heading,
  Icon,
  Input,
  InputForm,
  Modal,
  Spacer,
  Text,
  TextArea
} from "src/components";
import { ConfirmationDialog } from "src/components/confirmDialog";
import { theme } from "src/config";
import { Agenda } from "src/models/agenda";
import { AgendaStore } from "src/stores/agendaStore";
import styled from "styled-components";
import { Card } from "./card";

export interface IAgendaCardProps {
  onClick?(): void;
  onUnlink?(): void;
  data:         Agenda;
  agendaStore?: AgendaStore;
}

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;

const InputContainer = styled.div`
  flex: 1;
  align-content: left;
`;

const CardContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  justify-content: flex-end;
`;

export const AgendaCard = inject("agendaStore")(
  observer((props: IAgendaCardProps): JSX.Element => {
    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
    const [showEditModal, setShowEditModal] = React.useState(false);
    const { t, i18n } = useTranslation();
    const { data, agendaStore } = props;

    return (
      <Card
        width        = "22rem"
        height       = "18rem"
        body         = {<Text size="default">{t("agenda")}</Text>}
        expandedBody = {
          <CardContainer>
            <InputContainer>
              <Input
                value    = {data.title}
                label    = {t("name")}
                onBlur   = {() => data.save()}
                onChange = {(val) => data.set("title", val)}
                editOnFocus
                autoFocus
              />
              <Spacer />
              <Box>
                <Heading type="h3">{t("description")}</Heading>
                <Text size="small">{""}</Text>
              </Box>
            </InputContainer>
            <ButtonContainer>
              <Modal show={showConfirmDialog}>
                <ConfirmationDialog
                  onConfirm = {() => {data.remove(); agendaStore.setActive(null);}}
                  onCancel = {() => setShowConfirmDialog(false)}
                  label    = {t("confirm_action_tag")}
                  message  = {t("confirm_action_tag_message")}
                />
              </Modal>
              <Button variant="icon" onClick={() => setShowConfirmDialog(true)}>
                <TrashIcon color={theme.color.black} />
              </Button>
              {props.onUnlink && (
                <Button variant="icon" onClick={() => props.onUnlink()}>
                  <UnlinkIcon color={theme.color.black} />
                </Button>
              )}
              <Modal show={showEditModal}>
                <Box width="40rem" padding="2rem 2rem">
                  <Heading type="h2">{t("edit_agenda")}</Heading>
                  <Spacer size="small" />
                  <InputForm>
                    <Input label={t("title")} />
                    <TextArea
                      onChange     = {() => {}}
                      maxLength    = {200}
                      label        = {t("description")}
                      defaultValue = {"agendas dont have a desc yet"}
                    />
                  </InputForm>
                  <Text size="small">{t("duration")}</Text>
                  {/* here comes the time picker, which we have to fix */}
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant = "outlined"
                      onClick = {() => setShowEditModal(false)}>
                      {t("cancel")}
                    </Button>
                    <Spacer />
                    <Button>{t("save")}</Button>
                  </Box>
                </Box>
              </Modal>
            </ButtonContainer>
          </CardContainer>
        }
        key      = {data.id}
        editable = {true}
        expanded = {Boolean(agendaStore.getActive && (data.id === agendaStore.getActive.id))}
        onClick  = {props.onClick || ((e) => agendaStore.setActive(data))}
      />
    );
  })
);
